/* MolCellQuest 問題データ — 生体膜・輸送・オルガネラ
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "cell", category: "organelle", categoryName: "生体膜・輸送・オルガネラ" };

export const CELL_ORGANELLE = [
  {
    id: "org01", ...CAT,
    title: "生体膜の基本構造", diff: 90, tags: ["脂質二重層", "両親媒性", "流動モザイクモデル"],
    explain: `<p>細胞膜がリン脂質の二重層になる理由として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "リン脂質が両親媒性で、親水性の頭部を水に向け、疎水性の尾部どうしを内側で向かい合わせるため。",
      "リン脂質どうしが共有結合で強く結びついているため。",
      "リン脂質がすべて疎水性で、水をはじくため。",
      "タンパク質がリン脂質を2列に並べているため。",
    ],
    answer: 0,
    hints: [
      "リン脂質の分子は、水になじむ部分となじまない部分の両方を持っています。",
      "水中で最も安定な配置を考えましょう。疎水性の尾部は水から隠れたいはずです。",
    ],
    solution: `<p><b>【ステップ1】リン脂質の構造</b></p>
               <p>リン脂質は<b>両親媒性</b>——ひとつの分子に相反する2つの性質を持ちます。</p>
               <ul>
                 <li><b>親水性の頭部</b>：リン酸基とコリンなど。水と水素結合できる</li>
                 <li><b>疎水性の尾部</b>：2本の脂肪酸鎖。水をはじく</li>
               </ul>
               <p><b>【ステップ2】水中で自発的に並ぶ</b></p>
               <p>水中では、疎水性の尾部が水と接する面積を最小にする配置が最も安定です。
               その結果、<b>頭部を外（水）に、尾部を内側で向かい合わせた二重層</b>が
               <b>自発的に</b>形成されます。誰かが並べているわけではありません。</p>
               <p><b>【ステップ3】結合の性質</b></p>
               <p>この集合を保っているのは<b>疎水性相互作用とファンデルワールス力</b>であって、
               共有結合ではありません。だから膜は<b>流動的</b>で、
               脂質もタンパク質も膜内を横方向に拡散できます（流動モザイクモデル）。
               小さな穴が空いても自己修復するのは、この自己集合性のおかげです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org02", ...CAT,
    title: "受動輸送と能動輸送", diff: 125, tags: ["拡散", "能動輸送", "濃度勾配"],
    explain: `<p>物質の膜輸送について、<b>能動輸送</b>に当てはまるものはどれか。</p>`,
    type: "radio",
    options: [
      "酸素分子が濃度の高い側から低い側へ、脂質二重層を直接通り抜ける。",
      "グルコースが輸送体を介して、濃度の高い側から低い側へ移動する。",
      "水がアクアポリンを通って浸透圧に従って移動する。",
      "Na⁺/K⁺-ATPアーゼが、ATPを使ってNa⁺を細胞外へ、K⁺を細胞内へ運ぶ。",
    ],
    answer: 3,
    hints: [
      "能動輸送の定義は「濃度勾配に逆らって運ぶ」ことです。そのためには何が必要でしょうか。",
      "エネルギー（ATP など）を使っているのはどれでしょう。",
    ],
    solution: `<p><b>【ステップ1】輸送の分類</b></p>
               <table>
                 <tr><th>種類</th><th>勾配</th><th>エネルギー</th><th>例</th></tr>
                 <tr><td>単純拡散</td><td>沿う</td><td>不要</td><td>O<sub>2</sub>, CO<sub>2</sub>, ステロイド</td></tr>
                 <tr><td>促進拡散</td><td>沿う</td><td>不要</td><td>GLUT によるグルコース輸送、アクアポリン</td></tr>
                 <tr><td><b>能動輸送</b></td><td><b>逆らう</b></td><td><b>必要</b></td><td>Na⁺/K⁺-ATPアーゼ、H⁺ポンプ</td></tr>
               </table>
               <p><b>【ステップ2】選択肢を分類する</b></p>
               <ul>
                 <li>A ⇒ 単純拡散。膜を直接通過、勾配に沿う。</li>
                 <li>B ⇒ 促進拡散。輸送体を使うが勾配に沿うので、エネルギーは不要。
                     <b>「タンパク質を介する＝能動輸送」ではない</b>点に注意。</li>
                 <li>C ⇒ 促進拡散（水チャネル）。</li>
                 <li><b>D ⇒ 能動輸送。</b>ATP1分子で Na⁺ 3個を外へ、K⁺ 2個を内へ、
                     いずれも<b>濃度勾配に逆らって</b>運びます。</li>
               </ul>
               <p><b>【ステップ3】Na⁺/K⁺ポンプの重要性</b></p>
               <p>正味で正電荷を細胞外に運ぶため<b>膜電位</b>を作り、
               その Na⁺ 勾配が二次性能動輸送（グルコースやアミノ酸の取り込み）の動力にもなります。
               ヒトの安静時エネルギーの2〜3割がこのポンプに使われているとされます。</p>
               <p>したがって、正解は <b>D</b> です。</p>`,
  },

  {
    id: "org03", ...CAT,
    title: "オルガネラの働き", diff: 115, tags: ["ゴルジ体", "小胞体", "リソソーム"],
    explain: `<p>オルガネラとその主な働きの組み合わせとして、<b>誤っている</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "粗面小胞体 — 分泌タンパク質・膜タンパク質の合成と折りたたみ",
      "ゴルジ体 — タンパク質の糖鎖修飾と選別・輸送",
      "リソソーム — 加水分解酵素による分解（内部は酸性）",
      "ペルオキシソーム — ATPの大量合成",
    ],
    answer: 3,
    hints: ["ATPを大量に作るのはどのオルガネラでしょうか。", "ペルオキシソームの名前は「過酸化物」に由来します。"],
    solution: `<p><b>【ステップ1】各オルガネラの働き</b></p>
               <ul>
                 <li><b>粗面小胞体</b>：リボソームが付着。分泌・膜・オルガネラ行きのタンパク質を合成し、
                     シャペロンが折りたたみ、N型糖鎖付加とジスルフィド結合形成が起こる。</li>
                 <li><b>ゴルジ体</b>：シス→トランスへ進みながら糖鎖を加工し、
                     行き先ごとに選別して小胞で送り出す「郵便局」。</li>
                 <li><b>リソソーム</b>：V型ATPアーゼでH⁺を汲み入れ内部を <b>pH 約4.5〜5</b> に保ち、
                     酸性で最適に働く加水分解酵素で分解する。</li>
                 <li><b>ペルオキシソーム</b>：極長鎖脂肪酸のβ酸化などを行い、
                     副産物の <b>H<sub>2</sub>O<sub>2</sub></b> をカタラーゼで水と酸素に分解する。
                     <b>ATP合成の場ではありません</b>。</li>
               </ul>
               <p><b>【ステップ2】ATPを作るのは</b></p>
               <p><b>ミトコンドリア</b>（と植物の葉緑体）です。
               ペルオキシソームでのβ酸化はミトコンドリアと違い、
               生じた電子を電子伝達系に渡さず直接酸素に渡すので、
               <b>ATPを作らず熱を出します</b>。</p>
               <p class="tiny">なおリソソームが酸性であることには安全上の意味もあります。
               万一酵素が細胞質（中性）に漏れても、酸性でしか働かないため自己消化が起こりにくいのです。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "org04", ...CAT,
    title: "シグナルペプチドとタンパク質の行き先", diff: 175, tags: ["シグナル仮説", "小胞体", "選別"],
    explain: `<p>細胞質で合成が始まったタンパク質が、小胞体へ運ばれる仕組みについて正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "翻訳が完全に終わってから、完成したタンパク質が小胞体膜を通過する。",
      "N末端のシグナル配列を SRP が認識し、リボソームごと小胞体膜へ運ばれ、翻訳しながら膜を通過する。",
      "小胞体行きのタンパク質は、C末端の KDEL 配列によって最初から小胞体で合成される。",
      "シグナル配列は細胞質の可溶性タンパク質にも必ず存在する。",
    ],
    answer: 1,
    hints: [
      "SRP（シグナル認識粒子）は何を認識し、何をするでしょうか。",
      "「共翻訳的輸送」という言葉が示すとおり、翻訳と輸送は同時に進みます。",
    ],
    solution: `<p><b>【ステップ1】共翻訳的輸送の流れ</b></p>
               <ol>
                 <li>細胞質の遊離リボソームで翻訳が始まる</li>
                 <li>N末端の<b>シグナル配列</b>（疎水性アミノ酸に富む15〜30残基）が顔を出す</li>
                 <li><b>SRP（シグナル認識粒子）</b>が結合し、翻訳を一時停止させる</li>
                 <li>SRP受容体を介して小胞体膜へドッキングし、トランスロコンに引き渡される</li>
                 <li>翻訳が再開し、伸びながら小胞体内腔へ<b>同時に</b>送り込まれる（共翻訳的）</li>
                 <li>シグナルペプチダーゼがシグナル配列を切り離す</li>
               </ol>
               <p><b>【ステップ2】選別の原則</b></p>
               <p>「行き先の指定が無ければ細胞質に残る」が基本です。
               シグナル配列は<b>行き先のある一部のタンパク質だけ</b>が持ちます。</p>
               <table>
                 <tr><th>行き先</th><th>目印</th></tr>
                 <tr><td>小胞体</td><td>N末端シグナル配列</td></tr>
                 <tr><td>核</td><td>核局在化シグナル（NLS、塩基性アミノ酸に富む）</td></tr>
                 <tr><td>ミトコンドリア</td><td>N末端の両親媒性ヘリックス</td></tr>
                 <tr><td>小胞体に<b>留まる</b></td><td><b>C末端 KDEL 配列</b>（ゴルジから回収される）</td></tr>
               </table>
               <p><b>【ステップ3】他の選択肢を退ける</b></p>
               <ul>
                 <li>A ⇒ 小胞体へは<b>翻訳しながら</b>入ります（ミトコンドリアや葉緑体は翻訳後輸送）。</li>
                 <li>C ⇒ KDEL は「小胞体に<b>留める</b>」ための回収シグナルで、送り込むための配列ではありません。</li>
                 <li>D ⇒ 細胞質に留まるタンパク質は、そもそもシグナル配列を持ちません。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "org05", ...CAT,
    title: "浸透圧と細胞の体積", diff: 155, tags: ["浸透圧", "等張", "溶血"],
    explain: `<p>ヒトの赤血球を、次の溶液にそれぞれ入れた。</p>
              <ol>
                <li>0.9% NaCl 水溶液（生理食塩水）</li>
                <li>蒸留水</li>
                <li>3% NaCl 水溶液</li>
              </ol>
              <p>それぞれで赤血球に起こることの組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "1 変化なし ／ 2 収縮 ／ 3 膨張・破裂",
      "1 変化なし ／ 2 膨張・破裂 ／ 3 収縮",
      "1 膨張 ／ 2 変化なし ／ 3 収縮",
      "1 収縮 ／ 2 膨張・破裂 ／ 3 変化なし",
    ],
    answer: 1,
    hints: [
      "水は「溶質濃度の低いほう」から「高いほう」へ移動します。",
      "生理食塩水は細胞内と浸透圧がつり合う濃度（等張）に作られています。",
    ],
    solution: `<p><b>【ステップ1】水の動く向きの原則</b></p>
               <p>水は<b>溶質濃度の低い側から高い側へ</b>移動します
               （＝水そのものの濃度が高い側から低い側へ）。</p>
               <p><b>【ステップ2】3つの条件を判定する</b></p>
               <table>
                 <tr><th>外液</th><th>張度</th><th>水の動き</th><th>結果</th></tr>
                 <tr><td>0.9% NaCl</td><td><b>等張</b></td><td>出入りがつり合う</td><td>変化なし</td></tr>
                 <tr><td>蒸留水</td><td><b>低張</b></td><td>外→内へ流入</td><td>膨張し、<b>溶血</b></td></tr>
                 <tr><td>3% NaCl</td><td><b>高張</b></td><td>内→外へ流出</td><td>収縮（<b>金平糖状</b>になる）</td></tr>
               </table>
               <p><b>【ステップ3】なぜ 0.9% なのか</b></p>
               <p>ヒトの血漿の浸透圧は約 290 mOsm/L。NaCl は Na⁺ と Cl⁻ に電離して
               粒子数が2倍になるため、0.9%（約154 mmol/L）で約 308 mOsm/L となり、
               ほぼ等張になります。点滴に生理食塩水を使うのはこのためです。</p>
               <p><b>【ステップ4】植物細胞との違い</b></p>
               <p>植物細胞は細胞壁があるので、低張液でも破裂せず<b>膨圧</b>が高まるだけです。
               高張液では原形質分離が起こります。動物細胞に壁が無いからこそ、
               体液の浸透圧を厳密に保つ必要があるのです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "org06", ...CAT,
    title: "細胞内共生説の根拠", diff: 195, tags: ["ミトコンドリア", "細胞内共生", "進化"],
    explain: `<p>ミトコンドリアと葉緑体が、かつて独立した細菌だったとする細胞内共生説。</p>
              <p>その根拠として<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "独自の環状DNAを持ち、細胞核とは独立に分裂する。",
      "独自のリボソームを持ち、その大きさや抗生物質感受性が細菌型である。",
      "二重の膜を持ち、内膜の脂質組成が細菌の細胞膜に似ている。",
      "ミトコンドリアの全タンパク質が、ミトコンドリア自身のDNAにコードされている。",
    ],
    answer: 3,
    hints: [
      "ミトコンドリアのDNAは非常に小さく、ヒトでは16,569塩基対しかありません。何個の遺伝子が入るでしょうか。",
      "ミトコンドリアのタンパク質の大半は、実は核にコードされ細胞質で作られてから運び込まれます。",
    ],
    solution: `<p><b>【ステップ1】細胞内共生説の主な根拠</b></p>
               <ul>
                 <li><b>独自の環状DNA</b>：細菌と同じ形式。ヒストンを持たない。</li>
                 <li><b>独自のリボソーム</b>：真核細胞質の 80S ではなく<b>70S（細菌型）</b>。
                     そのためクロラムフェニコールなど細菌用の抗生物質が効く。</li>
                 <li><b>二重膜</b>：内膜が元の細菌の細胞膜、外膜が宿主の飲み込んだ膜に由来。
                     内膜にはカルジオリピンなど細菌型の脂質が含まれる。</li>
                 <li><b>分裂で増える</b>：新規に作られるのではなく、既存のものが分裂して増える。</li>
               </ul>
               <p><b>【ステップ2】D が誤りである理由</b></p>
               <p>ヒトのミトコンドリアDNAはわずか <b>16,569 塩基対</b>で、
               コードするのは <b>13種類のタンパク質</b>（すべて電子伝達系の部品）、
               22種のtRNA、2種のrRNA だけです。<br>
               一方ミトコンドリアの構成タンパク質は<b>1000種類以上</b>。
               残りは<b>核にコードされ、細胞質で合成されてから運び込まれます</b>。</p>
               <p><b>【ステップ3】これはむしろ共生説を支持する</b></p>
               <p>興味深いことに、この事実は共生説の反証ではなく<b>補強</b>になります。
               共生の長い過程で、元の細菌が持っていた遺伝子の大半が
               <b>宿主の核ゲノムへ移動した（遺伝子転移）</b>と考えられるからです。
               実際、核ゲノムにはミトコンドリア由来の配列が多数見つかっています。
               「独立していたものが、後戻りできないほど宿主に統合された」痕跡なのです。</p>
               <p>したがって、適切でないのは <b>D</b> です。</p>`,
  },

  {
    id: "org07", ...CAT,
    title: "小胞輸送の方向とコートタンパク質", diff: 245, tags: ["COPI", "COPII", "クラスリン"],
    explain: `<p>小胞輸送に関わるコートタンパク質と、その担う輸送方向の組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "COPII：ゴルジ体 → 小胞体（逆行輸送）",
      "COPI：小胞体 → ゴルジ体（順行輸送）",
      "COPII：小胞体 → ゴルジ体（順行輸送）、COPI：ゴルジ体 → 小胞体（逆行輸送）",
      "クラスリン：小胞体 → ゴルジ体の輸送のみを担う",
    ],
    answer: 2,
    hints: [
      "「II が先（順行）、I が戻り（逆行）」と番号と向きが逆になっている点が引っかけです。",
      "クラスリンは細胞膜からの取り込み（エンドサイトーシス）とトランスゴルジ網からの輸送に働きます。",
    ],
    solution: `<p><b>【ステップ1】3種のコートを整理する</b></p>
               <table>
                 <tr><th>コート</th><th>経路</th><th>向き</th><th>主な役割</th></tr>
                 <tr><td><b>COPII</b></td><td>小胞体 → ゴルジ体</td><td>順行（anterograde）</td><td>新しく作られた積荷を送り出す</td></tr>
                 <tr><td><b>COPI</b></td><td>ゴルジ体 → 小胞体<br>ゴルジ層板間</td><td>逆行（retrograde）</td><td>KDEL を持つ小胞体常在タンパク質や膜成分の回収</td></tr>
                 <tr><td><b>クラスリン</b></td><td>細胞膜 → 内部<br>トランスゴルジ網 → エンドソーム</td><td>取り込み・選別</td><td>受容体介在エンドサイトーシス、リソソーム酵素の輸送</td></tr>
               </table>
               <p><b>【ステップ2】番号と向きが逆なのを覚える</b></p>
               <p>直感に反しますが、<b>COPII が「行き」、COPI が「戻り」</b>です。
               発見順に番号がついたため、機能の順序とは一致していません。</p>
               <p><b>【ステップ3】なぜ逆行輸送が要るのか</b></p>
               <p>順行輸送だけでは、小胞体の膜と常在タンパク質がゴルジ体へ流出し続けて枯渇してしまいます。
               COPI がこれを回収することで<b>各オルガネラの組成が保たれます</b>。
               KDEL 受容体はゴルジ体の弱酸性pHで KDEL 配列を捕まえ、
               小胞体の中性pHで放す——pH差を利用した巧妙な仕分けです。</p>
               <p>したがって、正解は <b>C</b> です。</p>`,
  },

  {
    id: "org08", ...CAT,
    title: "膜電位から Nernst 式を使う", diff: 260, tags: ["Nernst式", "平衡電位", "計算"],
    explain: `<p>ある細胞の K⁺ 濃度は細胞内 <b>140 mM</b>、細胞外 <b>14 mM</b> である。</p>
              <p>37℃ における K⁺ の平衡電位はおよそいくらか。<br>
              Nernst 式：E = 61.5 × log<sub>10</sub>( [外] / [内] ) mV（1価の陽イオン、37℃）</p>`,
    type: "radio",
    options: ["+61.5 mV", "-61.5 mV", "+90 mV", "-90 mV"],
    answer: 1,
    hints: [
      "[外]/[内] = 14/140 = 0.1 です。log₁₀(0.1) はいくつでしょうか。",
      "log₁₀(0.1) = -1 なので、E = 61.5 × (-1) となります。",
    ],
    solution: `<p><b>【ステップ1】比を求める</b></p>
               <p>[K⁺]<sub>外</sub> / [K⁺]<sub>内</sub> = 14 / 140 = <b>0.1</b></p>
               <p><b>【ステップ2】対数をとる</b></p>
               <p>log<sub>10</sub>(0.1) = <b>-1</b></p>
               <p><b>【ステップ3】Nernst 式に代入する</b></p>
               <p>E<sub>K</sub> = 61.5 × (-1) = <b>-61.5 mV</b></p>
               <p><b>【ステップ4】符号の意味を確かめる</b></p>
               <p>K⁺ は細胞内に多いので、濃度勾配に従って<b>外へ出ようとします</b>。
               正電荷が出ていくと細胞内は<b>負</b>に帯電します。
               この電気的な引き戻しが濃度勾配とつり合った点が平衡電位で、
               マイナスになるのは理にかなっています。</p>
               <p><b>【ステップ5】静止膜電位との関係</b></p>
               <p>実際のニューロンの静止膜電位（約 -70 mV）が E<sub>K</sub> に近いのは、
               静止時の膜が主に K⁺ を通すからです。ただし Na⁺ もわずかに漏れ込むため、
               E<sub>K</sub> よりやや正側にずれます。<br>
               ちなみに濃度比が10倍変わるごとに約 61.5 mV 動くので、
               この式は暗算でも扱えます。</p>
               <p>したがって、正解は <b>-61.5 mV (B)</b> です。</p>`,
  },

  {
    id: "org09", ...CAT,
    title: "リソソーム蓄積症から輸送経路を推論する", diff: 295, tags: ["I細胞病", "M6P", "実験推論"],
    explain: `<p>I細胞病（ムコリピドーシスII型）の患者細胞では、
              リソソームに本来あるべき加水分解酵素がほとんど無く、
              代わりに<b>血中に大量の同じ酵素が見つかる</b>。</p>
              <p>この観察から推定される原因として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "加水分解酵素をコードする遺伝子群がまとめて欠失している。",
      "酵素をリソソームへ送るための目印（マンノース6-リン酸）を付ける酵素が欠損している。",
      "リソソーム膜のV型ATPアーゼが働かず、内部が酸性にならない。",
      "粗面小胞体でのタンパク質合成そのものが停止している。",
    ],
    answer: 1,
    hints: [
      "酵素は「作られていない」のではなく「作られたが違う場所へ行った」のです。何が起きたと考えられますか。",
      "リソソーム行きの目印が付かないと、タンパク質は既定の経路——つまり分泌経路へ流れていきます。",
    ],
    solution: `<p><b>【ステップ1】観察が示していること</b></p>
               <p>重要なのは<b>「酵素は作られている」</b>ことです。血中に大量にあるのだから、
               遺伝子も翻訳も無事です。問題は<b>行き先の指定</b>にあります。</p>
               <p><b>【ステップ2】リソソーム酵素の正しい経路</b></p>
               <ol>
                 <li>粗面小胞体で合成され、N型糖鎖が付く</li>
                 <li>シスゴルジで <b>GlcNAc-1-リン酸転移酵素</b> がマンノース残基にリン酸を付ける</li>
                 <li>→ <b>マンノース6-リン酸（M6P）</b>という目印ができる</li>
                 <li>トランスゴルジ網の <b>M6P受容体</b>が捕まえ、クラスリン小胞でエンドソームへ</li>
                 <li>酸性環境で受容体から離れ、リソソームに届く</li>
               </ol>
               <p><b>【ステップ3】I細胞病で何が壊れているか</b></p>
               <p>ステップ2の <b>GlcNAc-1-リン酸転移酵素が欠損</b>しています。
               M6P という荷札が付かないため、M6P受容体は酵素を認識できません。</p>
               <p>ここで効いてくるのが<b>分泌経路が既定路である</b>という原則です。
               特別な行き先指定が無いタンパク質は、そのまま小胞体→ゴルジ→細胞膜と流れて
               <b>細胞外へ分泌されてしまいます</b>。これが「血中に大量にある」ことの説明です。</p>
               <p><b>【ステップ4】他の選択肢を退ける</b></p>
               <ul>
                 <li>A ⇒ 多数の酵素の遺伝子が同時に欠失することは考えにくく、
                     血中に酵素があることとも矛盾します。</li>
                 <li>C ⇒ 酸性化の障害なら酵素は<b>リソソーム内にあるが働かない</b>はずで、
                     血中に出る説明になりません。</li>
                 <li>D ⇒ 合成が止まっていたら、血中に大量の酵素は現れません。</li>
               </ul>
               <p class="tiny">「I細胞」の名は、分解されない物質が溜まった封入体（inclusion body）が
               顕微鏡で目立つことに由来します。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "org10", ...CAT,
    title: "オートファジーの役割", diff: 285, tags: ["オートファジー", "飢餓応答", "オートファゴソーム"],
    explain: `<p>オートファジー（マクロオートファジー）について、<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "隔離膜が細胞質成分を囲んでオートファゴソームを作り、リソソームと融合して分解する。",
      "栄養飢餓のときに誘導され、分解産物をアミノ酸として再利用する。",
      "損傷したミトコンドリアなど、特定の対象を選んで分解することもある。",
      "分解された成分は細胞外へ排出され、再利用されることはない。",
    ],
    answer: 3,
    hints: [
      "オートファジーが飢餓で誘導されることの意味を考えましょう。分解して何を得たいのでしょうか。",
      "「リサイクル」という言葉がぴったり当てはまります。",
    ],
    solution: `<p><b>【ステップ1】オートファジーの流れ</b></p>
               <ol>
                 <li>隔離膜（ファゴフォア）が現れ、細胞質の一部を囲みはじめる</li>
                 <li>閉じて<b>二重膜のオートファゴソーム</b>ができる</li>
                 <li>リソソームと融合してオートリソソームに</li>
                 <li>加水分解酵素が中身を<b>アミノ酸・脂肪酸・糖</b>に分解</li>
                 <li>分解産物が<b>細胞質へ運び出され、再利用される</b></li>
               </ol>
               <p><b>【ステップ2】D が誤りである理由</b></p>
               <p>最後の<b>再利用</b>こそがオートファジーの存在意義です。
               飢餓時に誘導されるのは、外から栄養が来ないときに
               <b>自分の一部を分解して材料を作り出す</b>ためです。
               排出してしまっては意味がありません。</p>
               <p>実際、新生児は出産直後に胎盤からの栄養が絶たれる数時間、
               オートファジーで得たアミノ酸によって生き延びていることが分かっています。</p>
               <p><b>【ステップ3】選択的オートファジー</b></p>
               <p>「無差別に飲み込む」だけではありません。</p>
               <ul>
                 <li><b>マイトファジー</b>：損傷ミトコンドリア（PINK1/Parkin が目印を付ける）</li>
                 <li><b>ゼノファジー</b>：細胞内に侵入した細菌</li>
                 <li><b>アグリファジー</b>：凝集したタンパク質</li>
               </ul>
               <p>この品質管理機能の破綻は、パーキンソン病などの
               神経変性疾患と関連づけられています。</p>
               <p class="tiny">オートファジーの仕組みの解明で、大隅良典が2016年のノーベル生理学・医学賞を受賞しました。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "org11", ...CAT,
    title: "コートタンパク質と低分子量GTPase", diff: 215, tags: ["COPI", "COPII", "クラスリン", "Sar1"],
    explain: `<p>小胞が出芽するとき、コートタンパク質は低分子量GTPaseによって膜に呼び込まれる。</p>
              <p>組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "Sar1 → COPII（小胞体からの出芽）、ARF1 → COPI（ゴルジからの逆行輸送）、ARF → AP2/クラスリン（細胞膜・トランスゴルジ網）",
      "ARF1 → COPII（小胞体からの出芽）、Sar1 → COPI（ゴルジからの逆行輸送）",
      "Sar1 → クラスリン（細胞膜からの取り込み）、ARF1 → COPII（小胞体からの出芽）",
      "低分子量GTPaseはコートの形成には関与せず、膜融合のときだけ働く。",
    ],
    answer: 0,
    hints: [
      "小胞体（ER）から出るときのGTPaseだけ名前が違います。",
      "COPI は「戻る」、COPII は「進む」と覚えます。",
    ],
    solution: `<p><b>【ステップ1】3つの組をまとめる</b></p>
               <table>
                 <tr><th>コート</th><th>GTPase</th><th>どこからどこへ</th></tr>
                 <tr><td><b>COPII</b></td><td><b>Sar1</b></td><td>小胞体 → ゴルジ体（<b>順行</b>）</td></tr>
                 <tr><td><b>COPI</b></td><td><b>ARF1</b></td><td>ゴルジ体 → 小胞体、ゴルジ槽間（<b>逆行</b>）</td></tr>
                 <tr><td><b>クラスリン</b>（＋アダプター）</td><td>ARF</td><td>細胞膜 → 細胞内（AP2）、トランスゴルジ網 → エンドソーム（AP1）</td></tr>
               </table>
               <p>ER から出るときだけ <b>Sar1</b>、それ以外は ARF 系、と覚えると混乱しません。</p>
               <p><b>【ステップ2】共通の流れ</b></p>
               <ol>
                 <li>GTPase が <b>GDP型 → GTP型</b> になり、膜に刺さる</li>
                 <li>コートタンパク質を細胞質から呼び込む</li>
                 <li>コートが膜を湾曲させ、小胞ができる</li>
                 <li>GTP が加水分解されると<b>コートが外れる</b>（脱コート）</li>
               </ol>
               <p><b>【ステップ3】くびり切りは誰か</b></p>
               <p>クラスリン被覆小胞では、<b>ダイナミン</b>（それ自体もGTPase）が
               小胞の首にらせん状に巻き付き、GTP加水分解に伴って<b>くびり切り（scission）</b>ます。
               ダイナミンの変異体では、首でつながったままの小胞が溜まります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org12", ...CAT,
    title: "SNARE による膜融合とその再利用", diff: 235, tags: ["SNARE", "膜融合", "NSF"],
    explain: `<p>輸送小胞が標的膜と融合するとき、v-SNARE と t-SNARE が複合体を作る。</p>
              <p>融合<b>後</b>に起こることとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "NSF と α-SNAP が結合し、ATPの加水分解エネルギーでSNARE複合体をほどいて、次の輸送に再利用できるようにする。",
      "SNARE複合体はプロテアソームで分解され、新しく合成し直される。",
      "SNARE複合体はそのまま膜に残り、二度と使われない。",
      "GTPの加水分解によってSNARE複合体が自発的に解離する。",
    ],
    answer: 0,
    hints: [
      "SNARE複合体は非常に安定なので、ほどくには外から力（エネルギー）が要ります。",
      "NSF は N-ethylmaleimide sensitive factor、ATPase です。",
    ],
    solution: `<p><b>【ステップ1】融合までの流れ</b></p>
               <ol>
                 <li>小胞側の <b>v-SNARE</b>（VAMP/シナプトブレビン）と標的膜側の <b>t-SNARE</b>
                 （シンタキシン、SNAP-25）が出会う</li>
                 <li>4本のらせんが<b>ジッパーのように束</b>になる（トランスSNARE複合体）</li>
                 <li>この巻き上がりの力で2枚の膜が強引に引き寄せられ、<b>融合</b>する</li>
               </ol>
               <p><b>【ステップ2】ほどく</b></p>
               <p>融合後のSNARE複合体（シスSNARE複合体）は非常に安定で、放っておくとほどけません。
               そこで <b>α-SNAP</b> がアダプターとして結合し、<b>NSF</b>（ATPase）が
               <b>ATP加水分解のエネルギー</b>で無理やり引き離します。
               こうして各SNAREが再び使える状態に戻ります。</p>
               <p><b>【ステップ3】狙われる応用</b></p>
               <p><b>ボツリヌス毒素（BoNT）と破傷風毒素</b>は、この SNARE を<b>切断する
               プロテアーゼ</b>です。神経終末でアセチルコリン含有小胞の開口分泌ができなくなり、
               筋弛緩が起こります。美容・治療で使われるボトックスはこの作用を利用しています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org13", ...CAT,
    title: "ボツリヌス毒素はどこを壊すか", diff: 275, tags: ["ボトックス", "SNARE", "神経筋接合部"],
    explain: `<p>ボツリヌス毒素（ボトックス）を注射すると筋肉が弛緩する。</p>
              <p>その作用機序として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "神経終末に取り込まれ、軽鎖のプロテアーゼ活性でSNAREタンパク質を切断し、アセチルコリンの開口分泌を阻害する。",
      "アセチルコリン受容体に結合して、受容体を直接ふさぐ。",
      "アセチルコリンエステラーゼを阻害し、アセチルコリンを分解できなくする。",
      "筋小胞体からのCa²⁺放出を促進し、筋を持続的に収縮させる。",
    ],
    answer: 0,
    hints: [
      "毒素は「受容体をふさぐ」タイプではなく、「酵素として何かを切る」タイプです。",
      "切られるのは小胞の融合装置です。",
    ],
    solution: `<p><b>【ステップ1】毒素の構造と侵入</b></p>
               <p>ボツリヌス毒素は<b>重鎖と軽鎖</b>からなります。重鎖が神経終末表面の受容体に結合して
               エンドサイトーシスで取り込まれ、<b>軽鎖（亜鉛依存性プロテアーゼ）</b>が細胞質へ出ます。</p>
               <p><b>【ステップ2】何を切るか</b></p>
               <table>
                 <tr><th>毒素</th><th>切断する標的</th></tr>
                 <tr><td>BoNT/A, /E</td><td><b>SNAP-25</b></td></tr>
                 <tr><td>BoNT/B, /D, /F, /G ・ 破傷風毒素</td><td><b>VAMP（シナプトブレビン）</b></td></tr>
                 <tr><td>BoNT/C</td><td><b>シンタキシン</b>, SNAP-25</td></tr>
               </table>
               <p>いずれも <b>SNARE</b> です。SNAREが切れると小胞が細胞膜と融合できず、
               <b>アセチルコリンが放出されない</b> → 神経筋接合部の伝達が絶たれ、筋が弛緩します。</p>
               <p><b>【ステップ3】破傷風毒素との違い</b></p>
               <p>分子の作用（VAMP切断）は同じですが、<b>働く場所</b>が違います。
               ボツリヌス毒素は末梢の神経筋接合部に留まって<b>弛緩性麻痺</b>を起こし、
               破傷風毒素は脊髄へ逆行輸送されて<b>抑制性</b>ニューロンの伝達を止めるため、
               結果として<b>強直性（痙性）麻痺</b>になります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org14", ...CAT,
    title: "M6P経路と I-cell 病", diff: 285, tags: ["M6P", "ライソゾーム病", "I-cell病"],
    explain: `<p>ライソゾーム酵素は、ゴルジ体で目印を付けられてライソゾームへ運ばれる。</p>
              <p><b>I-cell 病（ムコリピドーシスII型）</b>で起きていることとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "GlcNAc-リン酸転移酵素の欠損により M6P の目印が付かず、ライソゾーム酵素が細胞外へ分泌されてしまう。細胞内には消化されない基質が封入体として溜まる。",
      "M6P受容体の過剰発現により、ライソゾーム酵素が過剰に濃縮される。",
      "単一のライソゾーム酵素が欠損し、その基質だけが蓄積する。",
      "ライソゾームの膜が形成されず、酵素が細胞質に漏れ出す。",
    ],
    answer: 0,
    hints: [
      "I-cell の I は inclusion（封入体）です。細胞の中に何が見えるでしょうか。",
      "「目印を付ける酵素」が壊れると、目印を付けられる側すべてが影響を受けます。",
    ],
    solution: `<p><b>【ステップ1】正常な M6P 経路</b></p>
               <ol>
                 <li>ライソゾーム酵素は小胞体で合成され、N型糖鎖が付く</li>
                 <li>シスゴルジで <b>GlcNAc-リン酸転移酵素</b>がマンノースに GlcNAc-リン酸を付ける</li>
                 <li>GlcNAc が外され、<b>マンノース6-リン酸（M6P）</b>が露出する</li>
                 <li>トランスゴルジ網の <b>M6P受容体</b>が結合し、クラスリン小胞で後期エンドソームへ運ぶ</li>
                 <li>酸性pHで酵素が離れ、受容体はゴルジへ戻って再利用される</li>
               </ol>
               <p><b>【ステップ2】I-cell 病の要点</b></p>
               <p>欠損しているのは<b>個々の酵素ではなく、目印を付ける酵素そのもの</b>です。
               そのため<b>ほぼすべてのライソゾーム酵素が行き先を失い、まとめて細胞外へ分泌</b>されます。</p>
               <ul>
                 <li><b>血中</b>：ライソゾーム酵素の活性が<b>異常に高い</b>（外に出ているため）</li>
                 <li><b>細胞内</b>：消化されない物質が溜まり、光学顕微鏡で<b>封入体（inclusion body）</b>として見える</li>
               </ul>
               <p>「血中で高値、細胞内で欠乏」という一見矛盾した所見が診断の決め手であり、
               試験でもよく問われます。</p>
               <p><b>【ステップ3】単一酵素欠損型との対比</b></p>
               <p>Fabry病（αガラクトシダーゼA欠損）や Gaucher病のように<b>1つの酵素だけ</b>が欠けるタイプでは、
               その酵素の基質だけが蓄積します。I-cell 病は<b>輸送の仕組みの病気</b>である点が本質的に異なります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org15", ...CAT,
    title: "酵素補充療法の原理と限界", diff: 250, tags: ["ライソゾーム病", "酵素補充療法", "Fabry病"],
    explain: `<p>Fabry 病などのライソゾーム病に対して、欠損した酵素を点滴で補う<b>酵素補充療法</b>が行われる。</p>
              <p>この治療の説明として<b>誤っている</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "投与した酵素は血液脳関門を容易に通過するため、中枢神経症状にもよく効く。",
      "投与した酵素は細胞表面のM6P受容体を介して取り込まれ、エンドサイトーシスでライソゾームへ運ばれる。",
      "外来のタンパク質であるため、抗体（中和抗体）ができて効果が下がることがある。",
      "定期的な点滴を生涯続ける必要があり、費用が高額になる。",
    ],
    answer: 0,
    hints: [
      "酵素は分子量の大きなタンパク質です。脳へ届くでしょうか。",
      "「誤っているもの」を選ぶ問題です。",
    ],
    solution: `<p><b>【ステップ1】どうやって細胞に入るのか</b></p>
               <p>投与する組換え酵素には <b>M6P</b> が付いています。
               細胞表面にも M6P 受容体（カチオン非依存性M6P受容体）があるため、
               血中の酵素が結合し、<b>エンドサイトーシス</b>で取り込まれてライソゾームへ届きます。
               これが治療が成り立つ仕組みです（B は正しい）。</p>
               <p><b>【ステップ2】限界を整理する</b></p>
               <table>
                 <tr><th>限界</th><th>内容</th></tr>
                 <tr><td><b>血液脳関門</b></td><td>分子量の大きい酵素は<b>通れない</b> → <b>中枢神経症状には効きにくい</b></td></tr>
                 <tr><td>免疫原性</td><td>外来タンパク質に対する<b>中和抗体</b>ができ、効果が減弱・アレルギー反応</td></tr>
                 <tr><td>組織移行性</td><td>骨・軟骨・心臓弁など血流の乏しい組織に届きにくい</td></tr>
                 <tr><td>負担</td><td>生涯にわたる<b>定期点滴</b>、非常に高額</td></tr>
                 <tr><td>不可逆な障害</td><td>すでに起きた臓器障害は戻らない → <b>早期開始</b>が重要</td></tr>
               </table>
               <p>したがって、<b>誤っている</b>のは <b>A</b> です。</p>
               <p><b>【ステップ3】代替アプローチ</b></p>
               <p>中枢神経に届かない問題への対策として、
               基質を作る側を減らす<b>基質合成抑制療法</b>、変異酵素を安定化する<b>シャペロン療法</b>、
               <b>遺伝子治療</b>や造血幹細胞移植などが開発されています。</p>`,
  },

  {
    id: "org16", ...CAT,
    title: "小胞体ストレス応答 UPR の3センサー", diff: 280, tags: ["UPR", "IRE1", "PERK", "ATF6"],
    explain: `<p>小胞体に折りたたみ不全のタンパク質が溜まると、<b>UPR（小胞体ストレス応答）</b>が起こる。</p>
              <p>3つのセンサーとその働きの組み合わせとして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "IRE1：XBP1 mRNAのスプライシングによる転写因子の産生／PERK：eIF2αのリン酸化による全般的な翻訳の抑制／ATF6：ゴルジ体で切断されて転写因子となる",
      "IRE1：翻訳の全般的な抑制／PERK：XBP1 mRNAのスプライシング／ATF6：小胞体膜の脂質合成のみを担う",
      "3つともシャペロンの誘導だけを行い、翻訳量には影響しない",
      "3つとも直ちにアポトーシスを誘導し、細胞の回復には寄与しない",
    ],
    answer: 0,
    hints: [
      "PERK の P は phosphorylation。何をリン酸化するか思い出してください。",
      "IRE1 は RNase 活性を持つ、変わったセンサーです。",
    ],
    solution: `<p><b>【ステップ1】3経路の役割分担</b></p>
               <table>
                 <tr><th>センサー</th><th>活性</th><th>結果</th></tr>
                 <tr><td><b>IRE1</b></td><td>キナーゼ＋<b>RNase</b></td><td><b>XBP1</b> mRNA を非典型的にスプライシング → 転写因子 XBP1s → シャペロン・ERAD 因子を誘導</td></tr>
                 <tr><td><b>PERK</b></td><td>キナーゼ</td><td><b>eIF2α をリン酸化</b> → <b>全般的な翻訳を抑制</b>（＝流入量を減らす）。一方 ATF4 は選択的に翻訳される</td></tr>
                 <tr><td><b>ATF6</b></td><td>転写因子の前駆体</td><td>ゴルジ体へ移動し <b>S1P/S2P で切断</b> → 細胞質ドメインが核へ入り転写因子として働く</td></tr>
               </table>
               <p><b>【ステップ2】UPR の狙いは3つ</b></p>
               <ol>
                 <li><b>入ってくる量を減らす</b>（PERK：翻訳抑制）</li>
                 <li><b>処理能力を上げる</b>（IRE1・ATF6：シャペロンの誘導、小胞体の拡張）</li>
                 <li><b>不良品を捨てる</b>（ERAD の誘導）</li>
               </ol>
               <p><b>【ステップ3】それでもダメなら</b></p>
               <p>ストレスが持続すると <b>CHOP</b> などを介して<b>アポトーシス</b>へ切り替わります。
               「まず回復を試み、無理なら細胞を処分する」という二段構えです。
               平常時は3つのセンサーはいずれも <b>BiP</b> に結合して不活性に保たれており、
               BiP が変性タンパク質の方へ取られることで活性化する、と理解すると流れがつながります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org17", ...CAT,
    title: "ERAD ─ 小胞体から細胞質への逆行輸送", diff: 290, tags: ["ERAD", "ユビキチン", "プロテアソーム"],
    explain: `<p>小胞体で正しく折りたためなかったタンパク質は <b>ERAD</b> によって処分される。</p>
              <p>ERAD の流れとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "小胞体膜のチャネルを通って細胞質へ逆行輸送され、ユビキチン化されてプロテアソームで分解される。",
      "小胞体の内腔で直接プロテアソームによって分解される。",
      "オートファゴソームに包まれ、ライソゾームでのみ分解される。",
      "小胞体からゴルジ体を経て細胞外へ分泌され、体外へ排出される。",
    ],
    answer: 0,
    hints: [
      "プロテアソームがあるのは細胞質です。基質はどこにあるでしょうか。",
      "ERAD の D は degradation、AD は associated degradation です。",
    ],
    solution: `<p><b>【ステップ1】場所の問題を解く</b></p>
               <p>プロテアソームは<b>細胞質</b>にあり、分解すべき不良品は<b>小胞体内腔</b>にあります。
               この矛盾を解決するのが<b>逆行輸送（retrotranslocation / dislocation）</b>です。</p>
               <ol>
                 <li>糖鎖の刈り込みなどで「不良品」と判定される</li>
                 <li>小胞体膜のチャネルを通って<b>細胞質側へ引き出される</b>（p97/VCP が ATP を使って引っ張る）</li>
                 <li>膜上の <b>E3ユビキチンリガーゼ</b>により<b>ポリユビキチン化</b></li>
                 <li><b>26Sプロテアソーム</b>で分解</li>
               </ol>
               <p><b>【ステップ2】ユビキチン鎖の種類で行き先が変わる</b></p>
               <table>
                 <tr><th>ユビキチンの付き方</th><th>意味（行き先）</th></tr>
                 <tr><td><b>K48 ポリユビキチン鎖</b></td><td><b>プロテアソームでの分解</b></td></tr>
                 <tr><td><b>K63 鎖・モノユビキチン</b></td><td><b>エンドサイトーシス → ライソゾーム</b>、シグナル伝達、DNA修復</td></tr>
               </table>
               <p>ユビキチン化の酵素は <b>E1（活性化）→ E2（結合）→ E3（リガーゼ、基質特異性を決める）</b>
               の順に働きます。E3 の種類が非常に多いのは、基質を選ぶ役だからです。</p>
               <p><b>【ステップ3】破綻すると</b></p>
               <p>処理しきれない異常タンパク質が凝集すると細胞毒性を示します。
               ハンチントン病では、伸長したポリグルタミンをもつ<b>ハンチンチン</b>が凝集体を作り、
               ユビキチン-プロテアソーム系を圧迫することが知られています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org18", ...CAT,
    title: "ペルオキシソームへのタンパク質輸送", diff: 245, tags: ["ペルオキシソーム", "PTS1", "Pex5p"],
    explain: `<p>ペルオキシソームの基質タンパク質は、細胞質で完成してから運び込まれる。</p>
              <p>その輸送について正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "C末端の PTS1（SKL 配列）を Pex5p が認識して膜の Pex14p へ運び、シグナル配列は切断されずに残る。折りたたまれた状態のまま輸送できる。",
      "N末端のシグナル配列を SRP が認識し、翻訳と同時に膜を通過させる。シグナル配列は切断される。",
      "ミトコンドリアと同じ Tom/Tim 複合体を使い、ほどけた状態で通過する。",
      "小胞体で合成された後、小胞輸送によってペルオキシソームへ届けられる。",
    ],
    answer: 0,
    hints: [
      "SKL（Ser-Lys-Leu）という3残基の配列が有名です。N末端でしょうか、C末端でしょうか。",
      "「折りたたんだまま運べる」という点が他のオルガネラと大きく違います。",
    ],
    solution: `<p><b>【ステップ1】オルガネラごとの輸送を対比する</b></p>
               <table>
                 <tr><th>行き先</th><th>シグナル</th><th>受容体・装置</th><th>状態</th><th>切断</th></tr>
                 <tr><td>小胞体</td><td>N末端シグナルペプチド</td><td>SRP → トランスロコン</td><td>翻訳と同時（ほどけている）</td><td><b>される</b></td></tr>
                 <tr><td>ミトコンドリア</td><td>N末端プレ配列</td><td>Tom/Tim</td><td>ほどけている</td><td><b>される</b></td></tr>
                 <tr><td>核</td><td>NLS</td><td>インポーチン、核膜孔</td><td><b>折りたたんだまま</b></td><td>されない</td></tr>
                 <tr><td><b>ペルオキシソーム</b></td><td><b>PTS1（C末端 SKL）</b>／PTS2（N末端）</td><td><b>Pex5p → Pex14p</b></td><td><b>折りたたんだまま</b>（多量体も可）</td><td><b>されない</b></td></tr>
                 </table>
               <p>ペルオキシソームと核が「折りたたんだまま・切断されない」組であることを押さえます。</p>
               <p><b>【ステップ2】受容体は往復する</b></p>
               <p>Pex5p は基質を細胞質で捕まえ、膜の Pex14p へドッキングして基質を放し、
               その後<b>細胞質へ戻って再利用</b>されます（受容体リサイクル）。</p>
               <p><b>【ステップ3】ペルオキシソームの仕事</b></p>
               <ul>
                 <li><b>極長鎖脂肪酸のβ酸化</b>（ミトコンドリアが扱えない長さを短くする）</li>
                 <li><b>プラズマローゲン</b>（エーテルリン脂質、髄鞘に多い）の合成</li>
                 <li>酸化反応で生じた<b>過酸化水素を、カタラーゼ</b>が水と酸素に分解する</li>
               </ul>
               <p>Pex 遺伝子の異常ではツェルウェガー症候群などのペルオキシソーム形成異常症を来します。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org19", ...CAT,
    title: "LDL受容体と家族性高コレステロール血症", diff: 210, tags: ["LDL受容体", "エンドサイトーシス", "コレステロール"],
    explain: `<p>LDL は受容体を介したエンドサイトーシスで細胞内に取り込まれる。</p>
              <p>この経路と家族性高コレステロール血症について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "LDL受容体はクラスリン被覆ピットに集まって取り込まれ、エンドソームの酸性環境でLDLを放して細胞膜へ戻る。受容体の遺伝的異常では血中LDLが上昇し、若年から動脈硬化を来す。",
      "LDL受容体はLDLと結合したままライソゾームで分解され、毎回新しく合成される必要がある。",
      "家族性高コレステロール血症では、LDL受容体が過剰に働いて細胞内にコレステロールが溜まる。",
      "LDLはマクロピノサイトーシスによって非特異的に取り込まれる。",
    ],
    answer: 0,
    hints: [
      "受容体はリサイクルされるか、分解されるか。エンドソームのpHが鍵です。",
      "受容体が働けないと、コレステロールは細胞に入れず、どこに残るでしょうか。",
    ],
    solution: `<p><b>【ステップ1】経路をたどる</b></p>
               <ol>
                 <li>LDL が細胞表面の <b>LDL受容体</b>に結合</li>
                 <li>受容体はアダプターを介して<b>クラスリン被覆ピット</b>に集まる</li>
                 <li>ダイナミンがくびり切り、被覆小胞ができる → 脱コート</li>
                 <li><b>初期エンドソーム（pH ≈ 6）</b>で受容体がLDLを<b>放す</b></li>
                 <li><b>受容体は細胞膜へリサイクル</b>、LDL はライソゾームでコレステロールに分解される</li>
               </ol>
               <p>「受け取ったら手放して戻る」というリサイクル型の代表例です。</p>
               <p><b>【ステップ2】病態</b></p>
               <p>受容体の遺伝子変異では、受容体が作られない／LDLに結合できない／
               クラスリンピットに集まれない、など段階のどこかが壊れます。
               いずれも結果は同じで、<b>LDLが細胞に取り込まれず血中に滞留</b>します。
               ヘテロ接合体でも若年からの<b>黄色腫・角膜輪・冠動脈疾患</b>を来し、
               ホモ接合体ではさらに重症です。</p>
               <p><b>【ステップ3】治療の考え方</b></p>
               <p>スタチンは細胞内コレステロール合成を抑えることで
               <b>LDL受容体の発現を増やし</b>、血中LDLを下げます。
               PCSK9 阻害薬は、LDL受容体を分解へ導く PCSK9 を抑えて<b>受容体のリサイクルを保ちます</b>。
               どちらも「受容体を増やす」方向の薬である点が共通しています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org20", ...CAT,
    title: "マクロピノサイトーシスとファゴサイトーシス", diff: 200, tags: ["エンドサイトーシス", "貪食", "アクチン"],
    explain: `<p>マクロピノサイトーシスとファゴサイトーシスを比べたとき、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "どちらもアクチン重合による膜の突出を伴うが、ファゴサイトーシスは粒子への受容体結合が引き金となり、マクロピノサイトーシスは受容体に依存せず細胞外液をまとめて取り込む。",
      "どちらもクラスリン被覆小胞を形成し、受容体に結合したリガンドだけを取り込む。",
      "マクロピノサイトーシスは固形の粒子を、ファゴサイトーシスは液体だけを取り込む。",
      "どちらもアクチンには依存せず、ダイナミンだけでくびり切られる。",
    ],
    answer: 0,
    hints: [
      "どちらも「大きく取り込む」経路で、細胞が膜を大きく張り出します。何がその力を出しているでしょうか。",
      "違いは「何をきっかけに始まるか」と「何を取り込むか」です。",
    ],
    solution: `<p><b>【ステップ1】表で対比する</b></p>
               <table>
                 <tr><th></th><th>ファゴサイトーシス（食作用）</th><th>マクロピノサイトーシス</th></tr>
                 <tr><td>取り込むもの</td><td><b>固形の粒子</b>（細菌、死細胞）</td><td><b>細胞外液</b>とその中の可溶性物質</td></tr>
                 <tr><td>引き金</td><td><b>受容体</b>への結合（Fc受容体、補体受容体など）</td><td><b>受容体非依存</b>（増殖因子刺激などで構成的に）</td></tr>
                 <tr><td>膜の動き</td><td>粒子に沿って<b>ファゴサイトーシスカップ</b>が伸びる（ジッパー様）</td><td><b>ラッフル</b>（波打つひだ）が閉じて袋を作る</td></tr>
                 <tr><td>アクチン</td><td><b>必要</b></td><td><b>必要</b></td></tr>
                 <tr><td>選択性</td><td>高い（結合したものだけ）</td><td>低い（周囲の液をまとめて）</td></tr>
                 <tr><td>行う細胞</td><td>主に<b>マクロファージ・好中球・樹状細胞</b></td><td>多くの細胞（樹状細胞の抗原採取など）</td></tr>
               </table>
               <p><b>【ステップ2】クラスリン経路との違い</b></p>
               <p>クラスリン依存性エンドサイトーシスは<b>直径100 nm 程度の小さな小胞</b>で、
               特定の受容体・リガンドを選んで取り込みます。
               一方この2つは<b>1 µm を超える</b>大きな取り込みで、
               コートではなく<b>アクチン細胞骨格の力</b>で膜を張り出す点が本質的に異なります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "org21", ...CAT,
    title: "分泌経路はどう証明されたか", diff: 265, tags: ["パルスチェイス", "Palade", "GFP"],
    explain: `<p>「分泌タンパク質が 小胞体 → ゴルジ体 → 分泌顆粒 → 細胞外 の順に運ばれる」ことを実験的に示す方法として、代表的なものを2つ挙げるとすればどれか。</p>`,
    type: "radio",
    options: [
      "放射性アミノ酸によるパルスチェイスと電子顕微鏡オートラジオグラフィー（Palade）／GFP融合タンパク質の蛍光顕微鏡によるライブイメージング",
      "ウェスタンブロットによる総量測定／PCRによる遺伝子量の測定",
      "細胞分画による全タンパク質の定量／SDS-PAGE による分子量測定",
      "X線結晶構造解析／質量分析によるアミノ酸配列決定",
    ],
    answer: 0,
    hints: [
      "証明したいのは「時間とともに場所が移ること」です。時間と場所の両方が分かる方法が要ります。",
      "古典的な方法と、現代的な方法が1つずつ。",
    ],
    solution: `<p><b>【ステップ1】何を示せばよいか</b></p>
               <p>示したいのは<b>「時間」と「場所」の対応</b>です。
               総量や分子量を測っても、どこにあるかは分かりません。B・C・D が外れるのはこのためです。</p>
               <p><b>【ステップ2】古典：Palade のパルスチェイス</b></p>
               <ol>
                 <li>膵外分泌細胞に <b>³H-ロイシン</b>を短時間だけ与える（<b>パルス</b>：この間に作られたタンパク質だけが標識される）</li>
                 <li>非標識のロイシンに置き換える（<b>チェイス</b>）</li>
                 <li>時間を追って固定し、<b>電子顕微鏡オートラジオグラフィー</b>で銀粒子の位置を見る</li>
               </ol>
               <p>銀粒子が <b>粗面小胞体 → ゴルジ体 → 分泌顆粒 → 細胞外</b> と移動していくのが観察され、
               分泌経路が確立しました（ノーベル賞対象）。</p>
               <p><b>【ステップ3】現代：GFP によるライブイメージング</b></p>
               <p>分泌タンパク質に <b>GFP</b> を融合させ、生きた細胞で蛍光を追跡します。
               温度感受性変異体（VSV-G ts045）を使い、低温で小胞体に溜めておいてから
               温度を戻すと、一斉に輸送が始まる様子をリアルタイムで観察できます。
               <b>固定標本の静止画（Palade）を、生きたまま動画にした</b>のが本質的な進歩です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },
];
